import { type FC, useState } from 'react';
import { Menu, MenuItem, OptionsMenuContainer } from '../styles/MenuButtonStyles';
import { useClickOutside } from '../../../../lib/hooks/useOnClickOutside';
import { Icon, IconType } from '../../../Props/Icon/Icon';
import { NamedContentEntryText } from '../../../ContentEntryText/NamedContentEntryText';
import NudgeGuidanceIcon from '../../assets/NudgeGuidance.svg';
import NudgeCongratsIcon from '../../assets/NudgeCongrats.svg';
import styled from 'styled-components';
import { type BehaviorLearnersCount, ScoreBreakdownType } from '../../../../lib/types/TeamBehavior';
import { NudgeType } from '../../../Nudge/enums/NudgeType';
import type { NudgeModalConfigs } from '../../../Nudge/NudgeModal';

export type NudgeOptionMenuProps = {
  learners: BehaviorLearnersCount;
  hideMenu?: boolean;
  setNudgeModalConfigs: (props: NudgeModalConfigs) => void;
  behavior: ScoreBreakdownType;
  disabled?: boolean;
};

export const NudgeOptionMenu: FC<NudgeOptionMenuProps> = ({
  learners,
  hideMenu = false,
  setNudgeModalConfigs,
  behavior,
  disabled = false,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const clickOutsideRef = useClickOutside<HTMLDivElement>(() => setIsMenuOpen(false));

  const onOptionClick = (type: NudgeType) => {
    if (disabled) {
      return;
    }

    setIsMenuOpen(false);
    setNudgeModalConfigs({
      isOpen: true,
      type: type,
      behavior: behavior,
    });
  };

  return (
    <OptionsMenuContainer ref={clickOutsideRef} $isHidden={hideMenu} $isOpen={isMenuOpen}>
      <div style={{ display: 'flex' }} onClick={() => setIsMenuOpen((prev) => !prev)}>
        <Icon icon={IconType.DOTS_MENU_ICON} width={16} height={16} />
      </div>
      <Menu $isHidden={!isMenuOpen} $placement="bottom-end">
        {learners.strong > 0 && (
          <MenuItem $disabled={disabled} onClick={() => onOptionClick(NudgeType.CONGRATS)}>
            <NudgeMenuItem>
              <LeftSection>
                <NudgeCongratsIcon />
                <NamedContentEntryText
                  container="span"
                  refKey="managerCongratulateNudgeDialog"
                  subKey="actionButtonText"
                />
              </LeftSection>
              <LearnersCount>{learners.strong} people</LearnersCount>
            </NudgeMenuItem>
          </MenuItem>
        )}
        {learners.weak > 0 && (
          <MenuItem $disabled={disabled} onClick={() => onOptionClick(NudgeType.GUIDANCE)}>
            <NudgeMenuItem>
              <LeftSection>
                <NudgeGuidanceIcon />
                <NamedContentEntryText
                  container="span"
                  refKey="managerSendGuidanceNudgeDialog"
                  subKey="actionButtonText"
                />
              </LeftSection>
              <LearnersCount>{learners.weak} people</LearnersCount>
            </NudgeMenuItem>
          </MenuItem>
        )}
      </Menu>
    </OptionsMenuContainer>
  );
};

const NudgeMenuItem = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const LeftSection = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const LearnersCount = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.normal};
  color: ${({ theme }) => theme.colors.normal};
`;
